import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Grid } from '@mui/material';
import colors from 'src/utils/colors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { RightArrow, DownChevron } from 'src/components/SvgIcons/SvgIcons';
import { useRequirementsModalStore } from '../stores/requirementsModalStore';
import { useProfileSummaryStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import {
  Guideline,
  BRAND_NAME_FORMATTING,
  AVOID_CROSS_CATEGORY_COMPARISONS,
  APPROVED_SUBSTITUTIONS,
  AVOID_SPECIFIC_CLAIM_LANGUAGE,
  APPROVED_NUTRIENT_RELATED_PHRASES,
  APPROVED_ON_PACKAGE_LANGUAGE,
  APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS,
  APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT,
  AVOID_RESTRICTED_TEXT_FOR_CLAIMS
} from '../types/profileTypes';
import { BrandNameFormattingCard } from './RequirementCards/BrandNameFormattingCard';
import { AvoidCrossCategoryComparisonsCard } from './RequirementCards/AvoidCrossCategoryComparisonsCard';
import { ApprovedSubstitutionsCard } from './RequirementCards/ApprovedSubstitutionsCard';
import { AvoidSpecificClaimLanguageCard } from './RequirementCards/AvoidSpecificClaimLanguageCard';
import { ApprovedNutrientRelatedPhrasesCard } from './RequirementCards/ApprovedNutrientRelatedPhrasesCard';
import { ApprovedOnPackageLanguageCard } from './RequirementCards/ApprovedOnPackageLanguageCard';
import { ApprovedQualifierPhrasesBeforeClaimsCard } from './RequirementCards/ApprovedQualifierPhrasesBeforeClaimsCard';
import { ApprovedProductAttributeAndDisclaimerTextCard } from './RequirementCards/ApprovedProductAttributeAndDisclaimerTextCard';
import { AvoidRestrictedTextForClaimsCard } from './RequirementCards/AvoidRestrictedTextForClaimsCard';

interface RequirementPanelProps {
  guideline: Guideline;
  index: number;
}

export const RequirementPanel: React.FC<RequirementPanelProps> = ({ guideline, index }) => {
  const { openModal } = useRequirementsModalStore();
  const isReadOnly = useProfileSummaryStore((s) => s.isReadOnly);

  const handleEditClick = () => {
    if (!isReadOnly) {
      openModal(index);
    }
  };

  // Render specific card based on guideline type
  switch (guideline.type) {
    case BRAND_NAME_FORMATTING:
      return <BrandNameFormattingCard guideline={guideline} index={index} onEditClick={handleEditClick} />;

    case AVOID_CROSS_CATEGORY_COMPARISONS:
      return <AvoidCrossCategoryComparisonsCard guideline={guideline} index={index} onEditClick={handleEditClick} />;

    case APPROVED_SUBSTITUTIONS:
      return <ApprovedSubstitutionsCard guideline={guideline} index={index} onEditClick={handleEditClick} />;

    case AVOID_SPECIFIC_CLAIM_LANGUAGE:
      return <AvoidSpecificClaimLanguageCard guideline={guideline} index={index} onEditClick={handleEditClick} />;

    case APPROVED_NUTRIENT_RELATED_PHRASES:
      return <ApprovedNutrientRelatedPhrasesCard guideline={guideline} index={index} onEditClick={handleEditClick} />;

    case APPROVED_ON_PACKAGE_LANGUAGE:
      return <ApprovedOnPackageLanguageCard guideline={guideline} index={index} onEditClick={handleEditClick} />;

    case APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS:
      return (
        <ApprovedQualifierPhrasesBeforeClaimsCard guideline={guideline} index={index} onEditClick={handleEditClick} />
      );

    case APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT:
      return (
        <ApprovedProductAttributeAndDisclaimerTextCard
          guideline={guideline}
          index={index}
          onEditClick={handleEditClick}
        />
      );

    case AVOID_RESTRICTED_TEXT_FOR_CLAIMS:
      return <AvoidRestrictedTextForClaimsCard guideline={guideline} index={index} onEditClick={handleEditClick} />;

    // Default fallback for other types (not yet implemented)
    default:
      return (
        <Accordion
          defaultExpanded={index === 0}
          sx={{
            boxShadow: 'none',
            overflow: 'hidden',
            border: `1px solid ${colors.graysSecondary}`,
            borderRadius: '8px !important',
            padding: '16px',
            '&:before': {
              display: 'none'
            },
            '&.Mui-expanded': {
              margin: 0,
              paddingBottom: 0
            }
          }}
        >
          <AccordionSummary
            expandIcon={<DownChevron width={24} height={24} style={{ transition: 'transform 0.2s ease' }} />}
            sx={{
              minHeight: 'unset !important',
              padding: '0px',
              '& svg': {
                width: '24px',
                height: '24px',
                color: colors.darkBlue
              },
              '&.Mui-expanded': {
                minHeight: 'unset',
                margin: '0px',
                paddingBottom: '16px',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  left: '-20px',
                  bottom: '0px',
                  width: '2000px',
                  height: '1px',
                  backgroundColor: colors.graysSecondary,
                  zIndex: '1'
                }
              },
              '& .MuiAccordionSummary-content': {
                margin: '0px !important'
              }
            }}
          >
            <Typography variant="h6">{guideline.name}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '24px 0', height: '150px', boxSizing: 'border-box' }}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="subtitle1">Title</Typography>
                <Typography sx={{ lineHeight: '30px' }}>{guideline.name}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle1">Requirement Type</Typography>
                <Typography sx={{ lineHeight: '30px' }}>{guideline.type}</Typography>
              </Grid>
              <Grid item xs={12} sx={{ marginTop: '16px' }}>
                <div
                  role="button"
                  style={{
                    width: 'fit-content',
                    cursor: isReadOnly ? 'default' : 'pointer'
                  }}
                  onClick={handleEditClick}
                >
                  <Text
                    variant="body2"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: isReadOnly ? colors.grey : 'inherit',
                      cursor: isReadOnly ? 'default' : 'pointer',
                      ...(!isReadOnly && {
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      })
                    }}
                  >
                    Edit requirement
                    <RightArrow
                      style={{
                        height: '12px',
                        width: '12px',
                        color: isReadOnly ? colors.grey : 'inherit'
                      }}
                    />
                  </Text>
                </div>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      );
  }
};
